import { IntlShape } from 'react-intl'

import {
  addDays,
  addWeeks,
  differenceInCalendarDays,
  differenceInDays,
  differenceInHours,
  differenceInMonths,
  differenceInYears,
  formatISO,
  formatRelative,
  isToday,
  isTomorrow,
  isValid,
  isWithinInterval,
  parse,
  parseISO,
} from 'date-fns'
import { format, utcToZonedTime } from 'date-fns-tz'
import { CountryCode } from 'libphonenumber-js'
import { capitalize, isNil } from 'lodash-es'

import { Appointment } from '../../../models/appointment/Appointment'
import { utilizationQuarters } from '../../../models/providers/Providers'
import { getDateFromAppointment } from '../../appointments/getDateFromAppointment'

export enum TimeUnit {
  DAYS = 'days',
  MONTHS = 'months',
  YEARS = 'years',
}

/**
 * @deprecated Please do not add any new usages of this!
 */
// Converts country ISO code to a date format
// e.g 'CA' -> 'YYYY-MM-DD'
//     'RU' -> 'DD/MM/YYYY'
// Note: This function was created as a temporary solution to address Canada's date format, which is 'YYYY/MM/DD' as opposed to
//       all other international date formats which uses 'DD/MM/YYYY'. This may not be the best solution moving forward as
//       date-fns/locale only returns a subset of locale objects that uses a date format that fits our needs and we would also
//       need to maintain localeMapper for any additional country ISO code to locale mappings. This solution is subject to
//       change for Intlv5 as we will be adding a language picker to more pages and may change the use case of this function
export const countryIsoCodeToDateFormat = (countryCode: CountryCode) => {
  // Only defining the countries that don't have format of DD/MM/YYYY
  const localeMapper: Partial<Record<CountryCode, string>> = {
    US: 'MM/DD/YYYY',
    CA: 'YYYY-MM-DD',
    DE: 'DD.MM.YYYY',
    PT: 'DD.MM.YYYY',
    PL: 'DD.MM.YYYY',
    JP: 'YYYY/MM/DD',
    KR: 'YYYY.MM.DD',
    CN: 'YYYY-MM-DD',
    NL: 'DD-MM-YYYY',
    CZ: 'DD.MM.YYYY',
    TW: 'YYYY-MM-DD',
    HK: 'YYYY-MM-DD',
    HU: 'YYYY.MM.DD',
    RO: 'DD.MM.YYYY',
    TR: 'DD.MM.YYYY',
  }
  const locale = localeMapper[countryCode]

  return locale || 'DD/MM/YYYY'
}

/**
 * Given a date string and date format, returns an object containing the month, day, and year from the date string
 * @param date A date string, e.g. '01/31/2023;
 * @param dateFormat A string representing the format of the date, e.g. 'MM/DD/YYYY'
 * @returns An object containing the month, day, and year of the given date, e.g. { month: '01', day: '31', year: '2023' }
 */
export const getDateParts = ({ date, dateFormat }: { date: string; dateFormat: string }) => {
  let month, day, year
  const delimiter = dateFormat[dateFormat.search(/[^A-Za-z]/)]
  const dateParts = date?.split(delimiter)
  const formatParts = dateFormat?.split(delimiter)

  formatParts?.forEach((part, index) => {
    switch (part) {
      case 'MM':
        month = dateParts[index]
        break
      case 'DD':
        day = dateParts[index]
        break
      case 'YYYY':
        year = dateParts[index]
        break
      default:
        break
    }
  })

  return {
    month,
    day,
    year,
  }
}

// Given a date string and a date format, converts the date to
// the domestic date format, MM/DD/YYYY
// e.g '1980/08/10', 'YYYY-MM-DD' -> '08/10/1980'
//     '08/10/1980', 'DD/MM/YYYY' -> '08/10/1980'
/**
 * @deprecated Please do not add any new usages of this!
 */
export const getDateInDomesticFormat = ({ date, dateFormat }: { date: string; dateFormat: string }) => {
  const { month, day, year } = getDateParts({ date, dateFormat })
  const domesticDate = [month, day, year].join('/')
  return domesticDate
}

/**
 * @deprecated Please do not add any new usages of this!
 *
 * Given a date string and date format, converts the date to ISO format (YYYY-MM-DD)
 * @param date A date string, e.g. '01/31/2023;
 * @param dateFormat A string representing the format of the date, e.g. 'MM/DD/YYYY'
 * @returns The given date string in ISO format, e.g. '2023-01-31' will be returned for { date: '01/31/2023', dateFormat: 'MM/DD/YYYY' }
 */
export const getDateInISOFormat = ({ date, dateFormat }: { date: string; dateFormat: string }) => {
  const { month, day, year } = getDateParts({ date, dateFormat })
  const isoDate = [year, month, day].join('-')
  return isoDate
}

/** @todo import { format } from 'date-fns' and make sure the dateFormat is in expected form: https://date-fns.org/v2.30.0/docs/Unicode-Tokens
 * @deprecated Please do not add any new usages of this!
 */
export const getDateInLocalizedFormat = ({
  date,
  currentDateFormat,
  desiredDateFormat,
}: {
  date: string
  currentDateFormat: string
  desiredDateFormat: string
}) => {
  const { month, day, year } = getDateParts({ date, dateFormat: currentDateFormat })
  const mapping: Record<string, string | undefined> = {
    MM: month,
    DD: day,
    YYYY: year,
  }
  const delimiter = desiredDateFormat[desiredDateFormat.search(/[^A-Za-z]/)]
  const dateFormatParts = desiredDateFormat?.split(delimiter)
  const formattedDate = [
    mapping[dateFormatParts[0].toUpperCase()],
    mapping[dateFormatParts[1].toUpperCase()],
    mapping[dateFormatParts[2].toUpperCase()],
  ].join(delimiter)
  return formattedDate
}

const DOB_LONG_FORMAT_OPTIONS: Intl.DateTimeFormatOptions = {
  month: 'long',
  day: 'numeric',
  year: 'numeric',
  timeZone: 'UTC',
}

/** Formats a date of birth for display, e.g. "April 28, 1995". Returns '' for missing dates. */
export const formatDateOfBirth = ({
  date,
  formatDate,
}: {
  date: string | null | undefined
  formatDate: IntlShape['formatDate']
}): string => {
  if (date == null || date === '') return ''
  return formatDate(date, DOB_LONG_FORMAT_OPTIONS)
}

const DISPLAY_DATE_FORMATS = {
  'DD MMM YYYY': 'dd MMM yyyy',
  'MM/DD/YYYY': 'MM/dd/yyyy',
  'YYYY-MM-DD': 'yyyy-MM-dd',
  'MMMM Do': 'MMMM do',
} as const

type DisplayDateFormat = keyof typeof DISPLAY_DATE_FORMATS
type DisplayDateInput = Date | string | number | { valueOf(): number } | null | undefined

const parseDisplayDate = (date: DisplayDateInput): Date => {
  if (date instanceof Date) return date

  if (typeof date === 'string') {
    const isoDate = parseISO(date)
    return isValid(isoDate) ? isoDate : new Date(date)
  }

  if (typeof date === 'number') return new Date(date)
  if (date && typeof date.valueOf() === 'number') return new Date(date.valueOf())

  return new Date(Number.NaN)
}

/** Formats a date using the legacy display formats still used by provider payments. */
export const getDisplayDate = (date: DisplayDateInput = '', dateFormat: DisplayDateFormat = 'DD MMM YYYY'): string => {
  const parsedDate = parseDisplayDate(date)
  return isValid(parsedDate) ? format(parsedDate, DISPLAY_DATE_FORMATS[dateFormat]) : 'Invalid date'
}

export const isWithin24Hours = (appointment: Appointment) => {
  const sessionStart = getDateFromAppointment(appointment)
  return differenceInHours(sessionStart, new Date()) < 24
}

export const isWithinXHoursInThePast = (datetime: Date, hours: number) => {
  const hourDifference = differenceInHours(new Date(), datetime)
  return 0 <= hourDifference && hourDifference <= hours
}

export const is24HoursInTheFuture = ({
  startDate,
  startTime,
  timeZone,
}: {
  startDate: string
  startTime: string
  timeZone: string
}) => {
  const futureDate = getDateFromAppointment({ startDate, startTime, timeZone })
  const hourDifference = differenceInHours(futureDate.getTime(), Date.now())
  return 0 <= hourDifference && hourDifference < 24
}

export const isWithinPastXMonths = (dateTime: Date, months: number) => {
  const monthsDifference = differenceInMonths(new Date(), dateTime)
  return 0 <= monthsDifference && monthsDifference < months
}

export const addUtcCalendarMonths = (timestamp: number, months: number): number => {
  const date = new Date(timestamp)
  const targetMonthIndex = date.getUTCMonth() + months
  const targetYear = date.getUTCFullYear() + Math.floor(targetMonthIndex / 12)
  const targetMonth = ((targetMonthIndex % 12) + 12) % 12
  const lastDayOfTargetMonth = new Date(Date.UTC(targetYear, targetMonth + 1, 0)).getUTCDate()

  return Date.UTC(
    targetYear,
    targetMonth,
    Math.min(date.getUTCDate(), lastDayOfTargetMonth),
    date.getUTCHours(),
    date.getUTCMinutes(),
    date.getUTCSeconds(),
    date.getUTCMilliseconds(),
  )
}

export const isWithinPastTimeFrame = (dateTime: Date, amount: number, unit: TimeUnit) => {
  const now = new Date()
  const diff =
    unit === TimeUnit.DAYS
      ? differenceInDays(now, dateTime)
      : unit === TimeUnit.MONTHS
        ? differenceInMonths(now, dateTime)
        : differenceInYears(now, dateTime)
  return 0 <= diff && diff < amount
}

export const getCurrentQuarter = (date?: Date) => {
  const dateObj = date ?? new Date()
  return Math.floor((dateObj.getUTCMonth() + 3) / 3)
}

export const getStartOfNextQuarterByWeeks = (utilizationQuarters: utilizationQuarters, date?: Date) => {
  const dateObj = date ?? new Date()
  const dateObjUTC = format(utcToZonedTime(dateObj, 'UTC'), 'yyyy-MM-dd')
  const sortedQuarters = Object.values(utilizationQuarters).sort()
  const quarterEndDate = sortedQuarters.find(
    (quarter: [string, string]) =>
      quarter[0].localeCompare(dateObjUTC) === -1 && dateObjUTC.localeCompare(quarter[1]) === -1,
  )
  return quarterEndDate
    ? addDays(new Date(`${quarterEndDate[1]}T00:00:00.000+00:00`), 1)
    : getStartOfNextQuarterByMonth(date)
}

export const getStartOfNextQuarterByMonth = (date?: Date) => {
  const dateObj = date ?? new Date()
  let newDateYear = dateObj.getFullYear()
  const nextQuarterFirstMonth = (getCurrentQuarter(dateObj) * 3 + 1) % 12
  if (getCurrentQuarter(dateObj) === 4) {
    newDateYear = dateObj.getFullYear() + 1
  }
  return new Date(`${newDateYear}-${nextQuarterFirstMonth.toString().padStart(2, '0')}-01T00:00:00.000+00:00`)
}

export const getStartOfNextPerformanceYear = (date?: Date) => {
  const dateObj = date ?? new Date()
  const newDateYear = dateObj.getFullYear() + 1
  return new Date(`${newDateYear}-01-01T00:00:00.000+00:00`)
}

export const getWeeksToDate = (endDate: Date, currentSetDate?: Date) => {
  const currentDate = currentSetDate ?? new Date()
  const timeToEndOfQuarter = endDate.getTime() - currentDate.getTime()
  const weekInMilliseconds = 604800000
  return Math.floor(timeToEndOfQuarter / weekInMilliseconds)
}

export const getDaysToDate = (endDate: Date, currentSetDate?: Date) => {
  const currentDate = currentSetDate ?? new Date()
  const timeToEndOfQuarter = endDate.getTime() - currentDate.getTime()
  const dayInMilliseconds = 86400000
  return Math.floor(timeToEndOfQuarter / dayInMilliseconds)
}

type DateClassification = 'today' | 'tomorrow' | 'week' | null
export function getClassificationForDate(dateTime: string): DateClassification | null {
  const date = new Date(dateTime)
  if (isToday(date)) {
    return 'today'
  }

  if (isTomorrow(date)) {
    return 'tomorrow'
  }

  if (isWithinInterval(date, { start: new Date(), end: addWeeks(new Date(), 1) })) {
    return 'week'
  }

  return null
}

export const convertFormatToDateFnsStandard = (dateFormat: string) => {
  return dateFormat.replace('YYYY', 'yyyy').replace('DD', 'dd').replace('mm', 'MM')
}

/**
 * Given an ISO 8601 date string like '2023-01-02', convert it to `MM/dd/yyyy` US format, `01/02/2023`.
 *
 * Use sparingly! This util should not be necessary, except to convert request payloads for
 * specific backend endpoints that still expect the US date format.
 *
 * If null or undefined, returns null or undefined
 */
export function convertDateStringFromISOToUSFormat<T extends null | undefined | never>(
  iso8601Date: string | T,
): string | T {
  if (iso8601Date === null || iso8601Date === undefined) {
    return iso8601Date
  }
  const parsedDate = parse(iso8601Date, 'MM/dd/yyyy', new Date())
  if (isValid(parsedDate)) {
    console.warn(`Date is already in US format - ${iso8601Date}`)
    return iso8601Date
  }
  return format(parseISO(iso8601Date), 'MM/dd/yyyy')
}

/**
 * Given a javascript date and timezone, create an ISO8601-compliant string with the timezone offset
 *
 * Ex: For Nov 28, 2023 at 9pm MST, this function returns the string 2023-11-28T21:00:00-07:00
 */
export function getISO8601DateWithTimezone({ date, timeZone }: { date: Date; timeZone?: string }) {
  return format(date, "yyyy-MM-dd'T'HH:mm:ssXXX", { timeZone })
}

export function localeToDatePartLabels(locale: string) {
  const displayNames = new Intl.DisplayNames(locale, { type: 'dateTimeField' })
  return Object.fromEntries(
    ['year', 'month', 'day'].map((part) => [part, capitalize(displayNames.of(part) ?? '')]),
  ) as {
    year: string
    month: string
    day: string
  }
}

export function localeToDatePartOrdering(locale: string) {
  const parts = new Intl.DateTimeFormat(locale)
    .formatToParts()
    .filter((part) => ['year', 'month', 'day'].includes(part.type))
    .map((part) => part.type as 'year' | 'month' | 'day')
  return parts
}

export function localeToDatePartsWithFormatStyles(
  locale: string,
  date: Date,
  yearFormat?: Intl.DateTimeFormatOptions['year'],
  monthFormat?: Intl.DateTimeFormatOptions['month'],
  dayFormat?: Intl.DateTimeFormatOptions['day'],
) {
  const formatMonth = new Intl.DateTimeFormat(locale, {
    month: monthFormat,
    timeZone: 'UTC',
  }).format
  const formatDay = new Intl.DateTimeFormat(locale, {
    day: dayFormat,
    timeZone: 'UTC',
  }).format
  const formatYear = new Intl.DateTimeFormat(locale, {
    year: yearFormat,
    timeZone: 'UTC',
  }).format
  return {
    month: formatMonth(date),
    day: formatDay(date),
    year: formatYear(date),
  }
}

/**
 * Returns a date object with today's /local/ date in UTC
 * localDateToday().toISOString() => '2024-02-16T00:00:00.000Z'
 * where 2024-02-16 is today's date in the local timezone.
 */
export function localDateToday(): Date {
  const local = new Date() // Returns timestamp using local timezone
  return new Date(Date.UTC(local.getFullYear(), local.getMonth(), local.getDate()))
}

export function getUTCDateISOString(utcDate: Date): string {
  return utcDate.toISOString().split('T')[0]
}

export function getFriendlyDate({
  date,
  addPreposition = false,
  displayTime = false,
  datePattern,
  trackEvent,
  relativeRecent = false,
  displayWeekdayForRecentDates = true,
}: {
  date?: Date | string
  addPreposition?: boolean
  displayTime?: boolean
  datePattern?: string
  trackEvent?: (event: string, eventProps?: any) => void
  relativeRecent?: boolean
  displayWeekdayForRecentDates?: boolean
}): string {
  if (isNil(date) || !isValid(new Date(date))) {
    trackEvent && trackEvent('getFriendlyDate is not valid', { date })
    return ''
  }
  if (Math.abs(differenceInCalendarDays(new Date(date), new Date())) <= 1) {
    return displayTime
      ? formatRelative(new Date(date), new Date())
      : formatRelative(new Date(date), new Date()).split(' at ')[0]
  }
  let dateFormat
  const calendarDays = Math.abs(differenceInCalendarDays(new Date(date), new Date()))

  if (calendarDays <= 7 && (relativeRecent || displayWeekdayForRecentDates)) {
    if (relativeRecent) {
      return calendarDays <= 1 ? '1 day ago' : `${calendarDays} days ago`
    } else {
      dateFormat = 'EEEE'
    }
  } else if (datePattern) {
    dateFormat = datePattern
  } else {
    dateFormat = Math.abs(differenceInYears(new Date(date), new Date())) <= 1 ? 'MMMM do' : 'MMMM do yyyy'
  }

  if (displayTime) {
    dateFormat += ', h:mm aaa'
  }

  return addPreposition ? `on ${format(new Date(date), dateFormat)}` : format(new Date(date), dateFormat)
}

// Converts US formatted date to ISO formatted date that is required for the DatePicker component
export function convertUSFormatToISO(dateString: string) {
  const isoDate = parseISO(dateString)

  if (!isValid(isoDate)) {
    const date = parse(dateString, 'MM/dd/yyyy', new Date())
    return formatISO(date, { representation: 'date' })
  }

  return dateString
}

// e.g. "Tue, Oct 14" takes year into account to get correct day of week.
export function formatDateToAvailabilityText({ year, month, day }: { year: number; month: number; day: number }) {
  const date = new Date(year, month, day)
  return new Intl.DateTimeFormat('en-US', {
    weekday: 'short', // Short weekday name (e.g., Tue)
    month: 'short', // Short month name (e.g., Oct)
    day: 'numeric', // Numeric day (e.g., 14)
  }).format(date)
}

/**
 * Parses a date string that may be in various formats:
 * - ISO format (YYYY-MM-DD) - used by mobile
 * - US locale format (MM/DD/YYYY) - e.g., "01/15/2026"
 * - UK/EU locale format (DD/MM/YYYY) - e.g., "15/01/2026"
 * - German locale format (DD.MM.YYYY) - e.g., "15.01.2026"
 *
 * Returns a Date object or undefined if the string is falsy or parsing fails.
 *
 * This handles the platform difference where:
 * - Mobile stores dates as ISO strings (e.g., "2026-01-15")
 * - Web stores dates as locale strings based on user's locale
 *
 * @param dateString - The date string to parse
 * @param locale - Optional locale string (e.g., "en-US", "en-GB") to determine format priority
 */
export function parseDateString(dateString: string | undefined, locale?: string): Date | undefined {
  if (!dateString) return undefined

  // First, try parsing as ISO format (YYYY-MM-DD) - most reliable
  const isoDate = parseISO(dateString)
  if (isValid(isoDate)) {
    return isoDate
  }

  // Order formats based on locale to avoid ambiguous date parsing
  // For example, "01/02/2026" could be Jan 2 (US) or Feb 1 (UK)
  const isUSLocale = locale?.startsWith('en-US')
  const formats = isUSLocale
    ? [
        'MM/dd/yyyy', // US format: 01/15/2026
        'dd/MM/yyyy', // UK/EU format: 15/01/2026
        'dd.MM.yyyy', // German format: 15.01.2026
        'yyyy/MM/dd', // Some Asian formats: 2026/01/15
      ]
    : [
        'dd/MM/yyyy', // UK/EU format: 15/01/2026 (most common internationally)
        'dd.MM.yyyy', // German format: 15.01.2026
        'MM/dd/yyyy', // US format: 01/15/2026
        'yyyy/MM/dd', // Some Asian formats: 2026/01/15
      ]

  for (const format of formats) {
    const parsedDate = parse(dateString, format, new Date())
    if (isValid(parsedDate)) {
      return parsedDate
    }
  }

  return undefined
}

/**
 * Converts a locale-specific date string to ISO format (YYYY-MM-DD).
 *
 * This function handles multiple date formats based on locale:
 * - US format: MM/DD/YYYY (e.g., "01/15/2026")
 * - UK/EU format: DD/MM/YYYY (e.g., "15/01/2026")
 * - German format: DD.MM.YYYY (e.g., "15.01.2026")
 * - ISO format: YYYY-MM-DD (returned as-is)
 *
 * If the date is already in ISO format, it returns the original string.
 * If parsing fails, returns the original string unchanged.
 *
 * @param dateString - The date string to convert
 * @param locale - Optional locale string (e.g., "en-US", "en-GB") to determine format priority
 * @returns The date in ISO format (YYYY-MM-DD), or the original string if parsing fails
 */
export function convertLocaleDateToISO(dateString: string | undefined, locale?: string): string | undefined {
  if (!dateString) return dateString

  // Check if already in ISO format (YYYY-MM-DD)
  const isoDate = parseISO(dateString)
  if (isValid(isoDate)) {
    return dateString
  }

  // Parse the locale-specific date string
  const parsedDate = parseDateString(dateString, locale)
  if (parsedDate && isValid(parsedDate)) {
    return formatISO(parsedDate, { representation: 'date' })
  }

  // Return original string if parsing fails
  return dateString
}
